import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";

// QR deep-link target. Login is enforced by the ProtectedRoute layout; the backend re-verifies
// company membership + permission and only ever returns THIS company's assignment. No customer data.
export default function LoadSheetViewer() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const r = await api.get(`/loading/assignments/${id}`);
      setData(r.data);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Unable to load this load sheet");
    } finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const openPdf = async () => {
    try {
      const r = await api.get(`/loading/assignments/${id}/load-sheet.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { setErr("Could not open the PDF"); }
  };

  if (loading) return <div className="p-6 text-slate-500" data-testid="loadsheet-loading">Loading load sheet…</div>;
  if (err) return (
    <div className="p-6 max-w-lg mx-auto" data-testid="loadsheet-error">
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">{err}</div>
    </div>
  );

  const a = data.assignment;
  const b = data.company?.branding || {};
  const primary = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(b.primary_color || "") ? b.primary_color : "#1e293b";
  const accent = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(b.accent_color || "") ? b.accent_color : "#2563eb";
  const cname = b.display_name || data.company?.name || "Company";
  const exc = a.departure_exception;
  const lines = a.lines || [];

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6" data-testid="loadsheet-viewer">
      {/* Branded header */}
      <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
        <div className="px-5 py-4 flex items-center gap-4" style={{ borderBottom: `3px solid ${accent}` }}>
          {b.logo_url
            ? <img src={b.logo_url} alt={cname} className="h-10 max-w-[160px] object-contain" data-testid="loadsheet-logo" />
            : <div className="text-xl font-bold" style={{ color: primary }} data-testid="loadsheet-company">{cname}</div>}
          <div className="ml-auto text-right">
            <div className="text-xs uppercase tracking-wide text-slate-400">Vehicle Load Sheet</div>
            <div className="text-sm font-semibold" style={{ color: primary }}>{a.vehicle_id}</div>
          </div>
        </div>

        {/* Meta */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 px-5 py-4 bg-slate-50 text-sm">
          <Meta label="Vehicle reg" value={a.vehicle_id} testid="ls-vehicle" />
          <Meta label="Run date" value={a.run_date} testid="ls-rundate" />
          <Meta label="Driver" value={a.driver_name || "—"} testid="ls-driver" />
          <Meta label="Status" value={a.status} testid="ls-status" />
        </div>

        {/* Departure banner */}
        {a.departed_at && (
          <div className={`px-5 py-3 text-sm ${exc ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-800"}`} data-testid="ls-departure">
            {exc
              ? <><b>Departed with missing items</b> at {a.departed_at} — authorised by {exc.approved_by_name}. Reason: {exc.reason}</>
              : <><b>Departed</b> at {a.departed_at} (all items loaded)</>}
          </div>
        )}

        {/* Lines */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ls-lines">
            <thead>
              <tr className="text-left text-white" style={{ backgroundColor: primary }}>
                <th className="px-3 py-2">SKU</th><th className="px-3 py-2">Product</th>
                <th className="px-3 py-2 text-right">Req</th><th className="px-3 py-2 text-right">Loaded</th>
                <th className="px-3 py-2 text-right">Missing</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((L) => {
                const miss = Math.max(0, (L.qty_required || 0) - (L.qty_loaded || 0));
                return (
                  <tr key={L.line_id} className="border-b border-slate-100" data-testid={`ls-line-${L.line_id}`}>
                    <td className="px-3 py-2 font-mono text-xs">{L.sku}</td>
                    <td className="px-3 py-2">{L.title}</td>
                    <td className="px-3 py-2 text-right">{L.qty_required}</td>
                    <td className="px-3 py-2 text-right">{L.qty_loaded || 0}</td>
                    <td className={`px-3 py-2 text-right font-semibold ${miss > 0 ? "text-amber-600" : "text-emerald-600"}`}>{miss}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="px-5 py-4 flex gap-3">
          <button onClick={openPdf} data-testid="ls-open-pdf"
            className="rounded-lg px-4 py-2 text-sm font-semibold text-white" style={{ backgroundColor: accent }}>
            View / download PDF
          </button>
        </div>
      </div>
      <p className="mt-3 text-xs text-slate-400">TEST MODE — not a delivery note. Load sheet {String(a.id).slice(0, 8)}.</p>
    </div>
  );
}

function Meta({ label, value, testid }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
      <div className="font-medium text-slate-800" data-testid={testid}>{value}</div>
    </div>
  );
}
